import { useTranslations } from '@vocab/react';
import {
  Box,
  Divider,
  IconChevron,
  Stack,
  Text,
  TextLinkButton,
  Accordion,
} from 'braid-design-system';
import { useEffect, useRef, useState } from 'react';

import CategoryGroupCard from 'src/components/atoms/CategoryGroupCard/CategoryGroupCard';
import CreateThreadCard from 'src/components/molecules/Shared/CreateThreadCard/CreateThreadCard';
import { getPagePaths } from 'src/core/domain/pages';
import { useAnalytics } from 'src/provider/analytics';
import { useConfig } from 'src/provider/config';
import { usePage } from 'src/provider/page';

import translations from './.vocab';
import MyGroups from './MyGroups';
import TopGroups from './TopGroups';

import * as styles from './Sidebar.css';

export function MobileSidebar() {
  const { t } = useTranslations(translations);
  const { trackAnalyticsInteraction } = useAnalytics();

  const { language } = useConfig();
  const {
    pageState: { pageName },
    pageDispatch,
  } = usePage();

  const defaultCategory = t('featured-threads');
  const topCategory = t('community-groups');
  const exploreGroupsCategory = t('explore-groups');
  const [isSidebarVisible, setSidebarVisible] = useState(false);

  const sidebarRef = useRef<HTMLDivElement | null>(null);

  const toggleSidebar = () => {
    setSidebarVisible((prev: any) => !prev);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(event.target as Node)
    ) {
      setSidebarVisible(false);
    }
  };

  const handleCreateThreadAnalytics = () => {
    toggleSidebar();
    trackAnalyticsInteraction('CreateThreadClick');
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Box className={styles.TextLinkButton}>
      <Text tone="formAccent">
        <TextLinkButton onClick={() => toggleSidebar()}>
          {topCategory}
          <IconChevron alignY="lowercase" direction="right" />
        </TextLinkButton>
      </Text>
      <Box
        ref={sidebarRef}
        className={`${styles.Sidebar} ${
          isSidebarVisible ? styles.SidebarVisible : ''
        }`}
      >
        <a
          className={
            pageName === 'CommunityHome'
              ? styles.SelectedFeaturedThreadsSidebar
              : styles.FeaturedThreadsSidebar
          }
          key={defaultCategory}
          href={`${getPagePaths({ language }).CommunityHome}`}
          onClick={() => {
            pageDispatch({ type: 'IS_LOADING' });
            setSidebarVisible(false);
          }}
        >
          <CategoryGroupCard
            key="default-category"
            category={defaultCategory}
          />
        </a>
        <a
          className={
            pageName === 'CommunityAllGroups'
              ? styles.SelectedSidebarExplorePage
              : styles.SidebarExplorePage
          }
          key={exploreGroupsCategory}
          href={`${getPagePaths({ language }).CommunityGroups}`}
          onClick={() => {
            pageDispatch({ type: 'IS_LOADING' });
            setSidebarVisible(false);
          }}
        >
          <CategoryGroupCard category={exploreGroupsCategory} />
        </a>
        <Stack space="small">
          <Divider />
          <CreateThreadCard
            handleCreateThreadAnalytics={handleCreateThreadAnalytics}
          />
        </Stack>
        <Box className={styles.AccordionItem}>
          <Accordion>
            <MyGroups />
            <TopGroups />
          </Accordion>
        </Box>
      </Box>
    </Box>
  );
}
